import { api, type PluginInfo, type SourceNovel, type NovelItem, type ChapterItem, type HistoryItem, type LibraryItem, type RecommendTarget, type Category } from '../api';
import { coverEl, coverGradient, qs, toast, switchToPrettyUrl, switchToPrettyUrlSync, sourceHref, getShareUrl } from '../utils';
import { getCachedSlug } from '../pretty-cache';
import { chapterUrlSuffix } from '../chapter-volume';
import { prettyWorkPath, guestCatalogHref } from '../pretty-path';
import { getGuestReadSet } from '../guest-read';
import { getCurrentUser, getAuthHint, getCurrentRole, canAccessByHint } from '../auth';
import { canAccess } from '../roles';
import { setHeroBackground } from '../img-fix';
import { mountLegalNoticeWhenLoaded } from '../legal';

const STATUS_LABELS: Record<string, string> = {
  Ongoing: 'Видається',
  Completed: 'Завершено',
  Licensed: 'Ліцензовано',
  'Publishing Finished': 'Публікацію завершено',
  Cancelled: 'Покинуто',
  OnHiatus: 'Призупинено',
  Unknown: 'Невідомо',
};

/**
 * Перетягування мишкою для горизонтальних overflow-x:auto рядків (жанри
 * нижче). Тач і трекпад скролять такі ряди самі, а звичайна мишка (клік і
 * тягнути) — ні, тож без цього ряд жанрів прогортався лише на телефоні.
 * Клас .dragging (style.css) лише міняє курсор на "grabbing" і вимикає
 * scroll-snap на час драгу — на сам скрол не впливає.
 */
function enableDragToScroll(el: HTMLElement) {
  let dragging = false;
  let moved = false;
  let startX = 0;
  let startScrollLeft = 0;

  el.addEventListener('mousedown', (e) => {
    dragging = true;
    moved = false;
    startX = e.pageX;
    startScrollLeft = el.scrollLeft;
    el.classList.add('dragging');
  });

  // На window, а не лише на el, — щоб драг не "губився", коли курсор за швидкого
  // руху вислизає за межі вузької стрічки, і щоб відпускання кнопки мишки поза
  // стрічкою все одно коректно завершувало драг.
  window.addEventListener('mousemove', (e) => {
    if (!dragging) return;
    const dx = e.pageX - startX;
    if (Math.abs(dx) > 3) moved = true;
    el.scrollLeft = startScrollLeft - dx;
  });

  function stopDrag() {
    if (!dragging) return;
    dragging = false;
    el.classList.remove('dragging');
  }
  window.addEventListener('mouseup', stopDrag);
  window.addEventListener('mouseleave', stopDrag);

  // Капсули жанрів зараз не клікабельні, але про всяк випадок (capture-фаза,
  // випереджає обробники самих капсул): клік одразу після драгу — не клік.
  el.addEventListener(
    'click',
    (e) => {
      if (moved) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
    true,
  );
}

/**
 * Гість (без входу): не бачить експорт в epub, рекомендації, "Додати" в бібліотеку,
 * перемикач "Приховувати прочитані" в сортуванні розділів, а виділення розділів
 * (довге натискання) для нього не працює. Кнопки ховає CSS (body.guest-mode у
 * style.css — він перебиває й display, який скрипт виставляє пізніше), а виділення
 * вимикається тут, у attachChapterRowInteractions. Сервер усі ці дії й так віддає
 * лише авторизованим (requireAuth у server.ts).
 */
let guestMode = false;

export async function initNovel() {
  const pluginId = qs('plugin');
  const path = qs('path');
  // Синхронно, ДО будь-яких запitів (навіть до перевірки логіну) — якщо
  // слаг цього твору вже відомий (кеш або сама сторінка відкрита за
  // коротким посиланням), адреса в рядку браузера одразу коротка, без
  // "спалаху" старого вигляду.
  if (pluginId && path) switchToPrettyUrlSync(pluginId, path);

  // Гостьовий вигляд (без кнопок бібліотеки тощо) вмикаємо СИНХРОННО за підказкою, не чекаючи
  // відповіді /api/auth/me, — інакше на мить (а з bfcache і довше) видно вигляд для авторизованого.
  if (getAuthHint() === 'guest') document.body.classList.add('guest-mode');
  // Кнопка "Перенести" (міграція на інше джерело) — лише для ролей з правом 'library.migrate' (не для гостьового
  // акаунта tier_1_subscriber). Ховаємо CSS-класом: код нижче сам перемикає display кнопки в кількох місцях,
  // а клас (!important) перебиває їх усі. Спершу за підказкою, щоб не блимнула, потім за точною роллю.
  document.body.classList.toggle('no-migrate', getAuthHint() === 'user' && !canAccessByHint('library.migrate'));
  void getCurrentRole().then((role) => document.body.classList.toggle('no-migrate', !!role && !canAccess(role, 'library.migrate')));
  guestMode = !(await getCurrentUser());
  document.body.classList.toggle('guest-mode', guestMode);
  const root = document.getElementById('novel-root') as HTMLElement;
  const empty = document.getElementById('empty') as HTMLElement;

  recommendState.pluginId = pluginId ?? '';
  recommendState.novelPath = path ?? '';

  // Звідки прийшли (Історія/Нове/Бібліотека) — щоб "назад" вело туди ж,
  // а не завжди на сторінку джерела. Прокидаємо це і далі — в посилання на
  // розділи, — щоб той самий контекст зберігався по всьому ланцюжку
  // Історія/Нове > Розділ > Сторінка твору > Історія/Нове,
  // Бібліотека > Сторінка твору > (Розділ >) Сторінка твору > Бібліотека.
  const fromParam = qs('from');
  const FROM_TARGETS: Record<string, string> = {
    history: '/history',
    updates: '/updates',
    library: '/library',
    recommendations: '/recommendations',
  };
  // Якщо прийшли з Бібліотеки — окремо запам'ятовуємо, в якій категорії, з
  // яким сортуванням і пошуковим запитом ми там були, щоб повернутись саме
  // туди (а не просто на бібліотеку з типовими значеннями). Прокидаємо ці ж
  // параметри і в посилання на розділ — щоб вони не загубились, навіть якщо
  // користувач звідси зайде в розділ і поверне назад сюди.
  const libReturnParams = new URLSearchParams();
  if (fromParam === 'library') {
    const typeParam = qs('type');
    const tabParam = qs('tab');
    const sortParam = qs('sort');
    const qParam = qs('q');
    if (typeParam) libReturnParams.set('type', typeParam);
    if (tabParam) libReturnParams.set('tab', tabParam);
    if (sortParam) libReturnParams.set('sort', sortParam);
    if (qParam) libReturnParams.set('q', qParam);
  }

  // Кнопка "Назад": хочемо повернутись саме туди, звідки прийшли. Для
  // Бібліотеки прокидаємо категорію/сортування/пошук через libReturnParams,
  // щоб href вів у той самий стан. Для "нагору" з browse.ts (нижче, гілка
  // cameFromBrowse) лишаємо history.back() — там дорога сюди завжди одним
  // прямим кроком, тож зайвих проміжних переходів у стеку не буває. Але для
  // History/Updates/Library так робити не можна: сюди часто потрапляють не
  // прямим переходом, а "нагору" з reader.html (кнопка "назад" у читалці),
  // а це завжди звичайний перехід за посиланням, тобто окремий НОВИЙ запис
  // в історії браузера. history.back() з novel.html у такому випадку
  // відкотився б лише на reader.html, а не на потрібну вкладку — тож тут
  // завжди йдемо за href.
  const backLink = document.getElementById('novel-back-link') as HTMLAnchorElement | null;
  if (backLink) {
    if (fromParam && FROM_TARGETS[fromParam]) {
      const targetBase = FROM_TARGETS[fromParam];
      const qsStr = libReturnParams.toString();
      backLink.href = fromParam === 'library' && qsStr ? `${targetBase}?${qsStr}` : targetBase;
      // ВАЖЛИВО: ніде тут НЕ підміняємо клік на history.back(), навіть для
      // Історії/Нового. Раніше тут була ідея "безкоштовного бонусу" (відновити
      // скрол через bfcache) саме для них, бо здавалось, що втрачати нічого.
      // Але це припущення хибне: сюди можна потрапити не лише прямим переходом
      // з history.html/updates.html, а і "нагору" з reader.html (кнопка
      // "назад" у читалці) — а це ЗАВЖДИ звичайний перехід за посиланням,
      // тобто окремий НОВИЙ запис в історії браузера. Тож history.back() з цієї
      // сторінки в такому випадку відкочується лише на reader.html (розділ),
      // а не на history.html/updates.html — саме так виникав баг, коли друге
      // натискання "назад" повертало на сторінку розділу замість вкладки
      // Історія. href, на відміну від history.back(), завжди веде туди, куди
      // й задумано, незалежно від того, скількома проміжними переходами (в
      // т.ч. prev/next розділами в читалці) користувач сюди дістався.
    } else if (guestMode) {
      // Гість: стрілка ЗАВЖДИ веде прямим посиланням на ту вкладку каталогу (/ або /titles), з якої він
      // прийшов, а не history.back() — той залежить від стеку історії (вкладки каталогу, підміна адреси
      // на коротку тощо) і міг відкотити на іншу сторінку. Каталог сам відновить список і прокрутку
      // (browse.ts: isReturningFromWorkPage — перехід зі сторінки твору).
      backLink.href = guestCatalogHref();
    } else {
      backLink.href = pluginId ? sourceHref(pluginId) : '/browse';
      backLink.addEventListener('click', (e) => {
        let cameFromBrowse = false;
        try {
          const refPath = new URL(document.referrer).pathname;
          cameFromBrowse =
            !!document.referrer &&
            (['/browse', '/browse.html', '/', '/titles'].includes(refPath) || refPath.startsWith('/plugin/'));
        } catch {
          /* некоректний referrer — ігноруємо */
        }
        if (cameFromBrowse && window.history.length > 1) {
          e.preventDefault();
          window.history.back();
        }
      });
    }
  }

  // Липка кнопка "назад" (з'являється при скролі) веде туди ж, куди й
  // оригінальна — просто перевикликаємо її клік, щоб не дублювати всю логіку
  // вище (fromParam/history.back/bfcache тощо).
  const stickyBackLink = document.getElementById('novel-sticky-back') as HTMLAnchorElement | null;
  if (backLink && stickyBackLink) {
    stickyBackLink.addEventListener('click', (e) => {
      e.preventDefault();
      backLink.click();
    });
  }

  if (!pluginId || !path) {
    root.style.display = 'none';
    empty.style.display = 'flex';
    return;
  }

  // --- Заголовок сторінки (обкладинка/назва/опис/жанри/автор/художник/
  // статус/джерело): рендериться в окрему функцію, бо викликається двічі —
  // спершу (миттєво) з кешу бібліотеки, якщо він є, і ще раз — коли
  // довантажаться повні/фінальні дані. Так сторінка твору, який уже в
  // бібліотеці, показується одразу, а не чекає списку розділів (він завжди
  // читається наживо — інакше нові розділи не виявити).
  const heroBgImg = document.getElementById('hero-bgimg') as HTMLElement;
  const heroCover = document.getElementById('hero-cover') as HTMLElement;
  const genreScroll = document.getElementById('genre-scroll') as HTMLElement;
  enableDragToScroll(genreScroll);
  const webviewBtn = document.getElementById('webview-btn') as HTMLAnchorElement;
  const descBox = document.getElementById('novel-desc') as HTMLElement;
  const descToggle = document.getElementById('desc-toggle') as HTMLElement;
  descToggle.addEventListener('click', () => descBox.classList.toggle('expanded'));

  const headInfoEl = document.getElementById('novel-headinfo') as HTMLElement;
  const metaLines = {
    author: document.getElementById('novel-line-author') as HTMLElement,
    country: document.getElementById('novel-line-country') as HTMLElement,
    translator: document.getElementById('novel-line-translator') as HTMLElement,
    artist: document.getElementById('novel-line-artist') as HTMLElement,
    status: document.getElementById('novel-line-status') as HTMLElement,
  };
  const aboutEl = document.getElementById('novel-about') as HTMLDetailsElement;
  const aboutDl = document.getElementById('about-dl') as HTMLElement;
  const isSmakolyky = pluginId === 'smakolykytl';

  // "Про твір": перші 10 с після розгортання текст виділяти не можна (клас about-locked, див. style.css) —
  // щоб випадковий тап/довге натискання при розгортанні не починало виділення. Закриття скидає таймер.
  const ABOUT_LOCK_MS = 10_000;
  let aboutLockTimer: ReturnType<typeof setTimeout> | undefined;
  aboutEl.addEventListener('toggle', () => {
    clearTimeout(aboutLockTimer);
    if (aboutEl.open) {
      aboutEl.classList.add('about-locked');
      aboutLockTimer = setTimeout(() => aboutEl.classList.remove('about-locked'), ABOUT_LOCK_MS);
    } else {
      aboutEl.classList.remove('about-locked');
    }
  });

  /** Показує/ховає металінію за наявністю тексту; dataset.has запам'ятовує, чи вона взагалі має вміст (fitMetaLines). */
  function setMetaLine(line: HTMLElement, text: string | undefined) {
    const span = line.querySelector('span') as HTMLElement;
    span.textContent = text ?? '';
    line.dataset.has = text ? '1' : '0';
    line.style.display = text ? '' : 'none';
  }

  /**
   * smakolykytl: якщо металінії не влазять по висоті в обкладинку (нижня межа останньої
   * видимої нижча за нижню межу .cardcover) — ховаємо їх за пріоритетом
   * Країна > Перекладач > Автор > Статус, доки не влізе. Спершу все, що має вміст, показуємо знову
   * (ширина вікна/шрифти могли змінитись).
   */
  function fitMetaLines() {
    if (!isSmakolyky) return;
    const hideOrder = [metaLines.country, metaLines.translator, metaLines.author, metaLines.status];
    const domOrder = [metaLines.author, metaLines.country, metaLines.translator, metaLines.status];
    for (const l of hideOrder) l.style.display = l.dataset.has === '1' ? '' : 'none';
    const cover = heroCover.querySelector('.cardcover') as HTMLElement | null;
    if (!cover) {
      syncAboutAuthor();
      return;
    }
    const coverBottom = () => cover.getBoundingClientRect().bottom + 0.5;
    const lastVisibleBottom = () => {
      const vis = domOrder.filter((l) => l.style.display !== 'none');
      return vis.length ? vis[vis.length - 1].getBoundingClientRect().bottom : 0;
    };
    for (const l of hideOrder) {
      if (lastVisibleBottom() <= coverBottom()) break;
      if (l.style.display !== 'none') l.style.display = 'none';
    }
    syncAboutAuthor();
  }

  /** Рядок "Автор" у "Про твір" (над "Країною") — видимий лише коли автора сховано з металінії шапки (fitMetaLines). */
  let aboutAuthorEls: HTMLElement[] = [];
  function syncAboutAuthor() {
    const hiddenInHeader = metaLines.author.dataset.has === '1' && metaLines.author.style.display === 'none';
    for (const el of aboutAuthorEls) el.style.display = hiddenInHeader ? '' : 'none';
    const anyVisible = Array.from(aboutDl.children).some((c) => (c as HTMLElement).style.display !== 'none');
    aboutEl.style.display = isSmakolyky && anyVisible ? '' : 'none';
  }
  // Обкладинка має фіксоване співвідношення сторін, тож її розмір не залежить від металіній — спостерігаємо
  // саме її (зміна ширини вікна / поява після display:none), без циклу.
  const metaResizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => fitMetaLines()) : null;
  window.addEventListener('resize', fitMetaLines);
  void document.fonts?.ready.then(fitMetaLines);

  /** Блок "Про твір" (лише smakolykytl). Рядок "Джерело" тут свідомо немає. */
  function renderAbout(data: Pick<SourceNovel, 'author' | 'altNames' | 'country' | 'year' | 'writingStatus' | 'translationStatus' | 'translator'>) {
    if (!isSmakolyky) {
      aboutEl.style.display = 'none';
      return;
    }
    const cap = (v?: string) => (v ? v.charAt(0).toUpperCase() + v.slice(1) : undefined);
    const rows: Array<[string, string | undefined, boolean?]> = [
      ['Інші назви', data.altNames],
      ['Автор', data.author, true], // показується лише якщо автора сховано з шапки (syncAboutAuthor)
      ['Країна', data.country],
      ['Рік випуску', data.year],
      ['Статус написання', cap(data.writingStatus)],
      ['Стан перекладу', cap(data.translationStatus)],
      ['Перекладач', data.translator],
    ];
    aboutDl.innerHTML = '';
    aboutAuthorEls = [];
    for (const [label, value, isAuthor] of rows) {
      if (!value) continue;
      const dt = document.createElement('dt');
      dt.textContent = label;
      const dd = document.createElement('dd');
      dd.textContent = value;
      if (isAuthor) {
        dt.style.display = 'none';
        dd.style.display = 'none';
        aboutAuthorEls = [dt, dd];
      }
      aboutDl.append(dt, dd);
    }
    syncAboutAuthor();
  }

  function renderHeader(
    data: Pick<SourceNovel, 'name' | 'cover' | 'author' | 'artist' | 'status' | 'summary' | 'genres' | 'url' | 'country' | 'translator' | 'translationStatus' | 'writingStatus' | 'altNames' | 'year'>,
    pluginLabel: string,
  ) {
    document.title = data.name;

    {
      const [from, to] = coverGradient(pluginId + path);
      const gradient = `linear-gradient(160deg, ${from}, ${to})`;
      // Фон — це CSS background без події помилки: робочу адресу (напр. з виправленою
      // кирилицею в імені файлу) підбирає setHeroBackground, а коли не працює жодна — градієнт.
      if (data.cover) setHeroBackground(heroBgImg, data.cover, gradient);
      else heroBgImg.style.backgroundImage = gradient;
    }
    heroCover.innerHTML = '';
    heroCover.appendChild(coverEl(data.name, data.cover, pluginId + path));

    (document.getElementById('novel-title') as HTMLElement).textContent = data.name;

    const statusLabel = STATUS_LABELS[data.status ?? 'Unknown'] ?? data.status ?? 'Невідомо';
    headInfoEl.classList.toggle('meta-fit', isSmakolyky);
    if (isSmakolyky) {
      // На сайті немає "Художника": Автор, Країна, Перекладач, Статус перекладу.
      // Рядок "Джерело" для smakolykytl поки не виводимо.
      setMetaLine(metaLines.author, data.author || 'Автор невідомий');
      setMetaLine(metaLines.country, data.country);
      setMetaLine(metaLines.translator, data.translator);
      setMetaLine(metaLines.artist, undefined);
      setMetaLine(metaLines.status, `Переклад ${(data.translationStatus || statusLabel).toLowerCase()}`);
    } else {
      // Автор/художник: якщо відоме лише одне з полів — показуємо його в обох рядках.
      const author = data.author || data.artist || '';
      const artist = data.artist || data.author || '';
      setMetaLine(metaLines.author, author || 'Автор невідомий');
      setMetaLine(metaLines.country, undefined);
      setMetaLine(metaLines.translator, undefined);
      setMetaLine(metaLines.artist, artist || 'Автор невідомий');
      setMetaLine(metaLines.status, `${statusLabel} • ${pluginLabel}`);
    }
    renderAbout(data);
    metaResizeObserver?.disconnect();
    const coverNode = heroCover.querySelector('.cardcover');
    if (coverNode) metaResizeObserver?.observe(coverNode);
    fitMetaLines();
    requestAnimationFrame(fitMetaLines);

    (document.getElementById('novel-desc-text') as HTMLElement).textContent = data.summary || 'Опис відсутній.';

    genreScroll.innerHTML = '';
    const genres = (data.genres || '')
      .split(',')
      .map((g) => g.trim())
      .filter(Boolean);
    for (const g of genres) {
      const pill = document.createElement('div');
      pill.className = 'genrepill';
      pill.textContent = g;
      genreScroll.appendChild(pill);
    }

    // WebView — відкриває сторінку твору на сайті джерела (з'являється,
    // тільки коли відоме реальне посилання: кешований знімок його не має).
    if (data.url) webviewBtn.href = data.url;
  }

  let plugins: PluginInfo[] = [];
  let pluginName = pluginId;
  let contentType: 'novel' | 'manga' | 'anime' = 'novel';
  try {
    plugins = await api.plugins();
    const info = plugins.find((p) => p.id === pluginId);
    if (info) {
      pluginName = info.name;
      contentType = info.contentType;
    }
  } catch {
    /* ignore */
  }

  const chaptersListEl = document.getElementById('chapters-list') as HTMLElement;
  const chaptersCountEl = document.getElementById('chapters-count') as HTMLElement;
  const readBtn = document.getElementById('read-btn') as HTMLAnchorElement;
  const filterBtn = document.getElementById('chapters-filter-btn') as HTMLElement;
  const list = document.getElementById('chapters-list') as HTMLElement;
  // Застереження про фан-переклад — лише для творів smakolykytl, під списком розділів (текст: public/legal.json).
  if (pluginId === 'smakolykytl' && list.parentElement) mountLegalNoticeWhenLoaded(list, list.parentElement, list);

  let libraryItem: LibraryItem | undefined;
  try {
    libraryItem = await api.libraryLookup(pluginId, path);
  } catch {
    /* твору немає в бібліотеці — це нормально */
  }

  // Статус "У бібліотеці" — одразу, з уже відомих даних бібліотеки (якщо
  // твір там є), а не лише тоді, коли (повільніше) довантажаться живі дані.
  if (libraryItem) {
    setupLibraryButton(libraryItem.id, libraryItem.name, libraryItem.cover, true, pluginId, path, contentType, libraryItem.categoryIds);
  }

  // Миттєвий показ зі СПІЛЬНОГО кешу (усі користувачі застосунку, не лише
  // цей акаунт) — і заголовок, і, головне, СПИСОК РОЗДІЛІВ (раніше тут
  // завжди чекали лише на живий запит). Список може бути ЗАСТАРІЛИМ — щойно
  // паралельно завершиться живий запит нижче, він перезапише і сам список,
  // і його показ, свіжими даними. Працює навіть для творів НЕ в бібліотеці
  // цього користувача, якщо їх раніше вже відкривав хтось інший.
  let cached: { novel: Omit<SourceNovel, 'chapters'> | null; chapters: ChapterItem[] | null; readChapters: string[] } | null = null;
  let historyList: HistoryItem[] = [];
  try {
    [cached, historyList] = await Promise.all([api.novelCached(pluginId, path), api.history().catch(() => [])]);
  } catch {
    /* нічого немає в кеші — це нормально, чекаємо на живі дані нижче */
  }

  let historyEntry: HistoryItem | undefined = historyList.find((h) => h.pluginId === pluginId && h.novelPath === path);
  const cachedHeader = libraryItem?.cachedNovel ?? cached?.novel ?? undefined;
  const cachedChapters = cached?.chapters ?? undefined;

  if (cachedHeader) {
    renderHeader(cachedHeader, pluginName);
    readBtn.style.display = 'none';
    recommendState.name = cachedHeader.name;
    recommendState.cover = cachedHeader.cover;
  }

  let sectionHandle: ChaptersSectionHandle | undefined;
  if (cachedChapters && cachedChapters.length) {
    const cachedReadSet = new Set(cached?.readChapters ?? []);
    if (guestMode) for (const p of getGuestReadSet(pluginId, path)) cachedReadSet.add(p);
    const cachedNovelView: SourceNovel = { name: '', path, ...cachedHeader, chapters: cachedChapters };
    recommendState.chapterCount = cachedChapters.length;
    paintReadButton(readBtn, pluginId, path, cachedNovelView, cachedChapters, cachedReadSet, historyEntry, contentType, fromParam, libReturnParams);
    setupStickyBar();
    sectionHandle = setupChaptersSection(
      pluginId,
      path,
      cachedNovelView,
      cachedChapters,
      cachedReadSet,
      () => historyEntry,
      filterBtn,
      list,
      chaptersCountEl,
      contentType,
      fromParam,
      libReturnParams,
      libraryItem?.id,
      libraryItem?.sortOrder,
    );
  } else {
    chaptersCountEl.textContent = 'Завантаження…';
    chaptersListEl.innerHTML = '<div class="chapters-loading">Завантаження списку розділів…</div>';
  }

  let novel: SourceNovel;
  try {
    novel = await api.novel(pluginId, path);
  } catch (err) {
    if (sectionHandle) {
      // Список розділів уже показано зі спільного кешу — просто
      // повідомляємо, що не вдалось оновити його свіжими даними, і лишаємо
      // (можливо, застарілий, але робочий) кешований список як є.
      toast(`Не вдалося оновити дані твору: ${(err as Error).message}`);
      return;
    }
    if (libraryItem?.cachedNovel) {
      toast(`Не вдалося завантажити розділи: ${(err as Error).message}`);
      chaptersCountEl.textContent = '0 розділів';
      chaptersListEl.innerHTML = '<div class="chapters-loading">Не вдалося завантажити список розділів.</div>';
      setupLibraryButton(libraryItem.id, libraryItem.name, libraryItem.cover, true, pluginId, path, contentType, libraryItem.categoryIds);
      setupMigration(pluginId, path, { ...libraryItem.cachedNovel, id: libraryItem.id }, plugins);
      return;
    }
    toast((err as Error).message);
    root.style.display = 'none';
    empty.style.display = 'flex';
    return;
  }

  if (novel.chaptersFailed) {
    toast('Джерело зараз недоступне — показано збережені дані бібліотеки без списку розділів');
  }

  renderHeader(novel, pluginName);
  readBtn.style.display = '';
  setupStickyBar();

  // Тихо підміняємо адресу в рядку браузера на коротке посилання
  // /<джерело>/<слаг> — далі "Поділитись" (location.href) копіює саме її.
  switchToPrettyUrl(pluginId, path, novel.name);

  const chapters = novel.chapters ?? [];
  recommendState.name = novel.name;
  recommendState.cover = novel.cover;
  recommendState.chapterCount = chapters.length;

  // "Прочитано" тепер визначається розділами, дочитаними на 100% (readChapters),
  // а не просто останнім відвіданим розділом.
  const readSet = new Set(novel.readChapters ?? []);
  // Гість: прочитане зберігається в браузері окремо для кожного твору (guest-read.ts).
  if (guestMode) for (const p of getGuestReadSet(pluginId, path)) readSet.add(p);

  // Перечитуємо історію ще раз (могла змінитись, поки виконувався живий
  // запит вище) — розділ "у процесі читання" має спиратись на найсвіжіший стан.
  try {
    const hist = await api.history();
    historyEntry = hist.find((h) => h.pluginId === pluginId && h.novelPath === path) ?? historyEntry;
  } catch {
    /* лишаємо те, що вже мали з кешованого показу — не критично */
  }

  paintReadButton(readBtn, pluginId, path, novel, chapters, readSet, historyEntry, contentType, fromParam, libReturnParams);

  if (sectionHandle) {
    sectionHandle.update(novel, chapters, readSet);
  } else {
    sectionHandle = setupChaptersSection(
      pluginId,
      path,
      novel,
      chapters,
      readSet,
      () => historyEntry,
      filterBtn,
      list,
      chaptersCountEl,
      contentType,
      fromParam,
      libReturnParams,
      novel.inLibrary ? novel.id : undefined,
      libraryItem?.sortOrder,
    );
  }

  // --- Бібліотека + Перенести (міграція на інше джерело) ---
  setupLibraryButton(novel.id!, novel.name, novel.cover, !!novel.inLibrary, pluginId, path, contentType, libraryItem?.categoryIds ?? []);
  setupMigration(pluginId, path, novel, plugins);
}

/** Обчислює й малює кнопку "Продовжити"/"Почати читання" — використовується і для швидкого (кешованого), і для живого показу. */
function paintReadButton(
  readBtn: HTMLAnchorElement,
  pluginId: string,
  novelPath: string,
  novel: SourceNovel,
  chapters: ChapterItem[],
  readSet: Set<string>,
  historyEntry: HistoryItem | undefined,
  contentType: 'novel' | 'manga' | 'anime',
  fromParam: string | null,
  libReturnParams: URLSearchParams,
) {
  if (!chapters.length) {
    readBtn.style.display = 'none';
    return;
  }
  readBtn.style.display = '';
  const inProgress =
    historyEntry && historyEntry.progress > 0 && historyEntry.progress < 100
      ? chapters.find((c) => c.path === historyEntry!.chapterPath)
      : undefined;
  // "Продовжити читання" — рівно два випадки:
  //  1) є розділ із прогресом (0 < % < 100, це останній відкритий з історії) — продовжуємо його (inProgress);
  //  2) інакше — розділ ПІСЛЯ останнього прочитаного (а не найперший непрочитаний, щоб твір, який
  //     почали з середини, не вів на 1-й розділ).
  // "Останній прочитаний": розділ з історії, якщо він дочитаний (100%) або є в readSet; інакше —
  // найдальший прочитаний за порядком списку. Якщо далі все прочитано — найперший непрочитаний.
  let nextUnread: ChapterItem | undefined;
  if (!inProgress) {
    let anchor = -1;
    const histIdx = historyEntry ? chapters.findIndex((c) => c.path === historyEntry!.chapterPath) : -1;
    if (histIdx >= 0 && (historyEntry!.progress >= 100 || readSet.has(chapters[histIdx].path))) {
      anchor = histIdx;
    } else {
      chapters.forEach((c, i) => {
        if (readSet.has(c.path)) anchor = i;
      });
    }
    nextUnread = chapters.slice(anchor + 1).find((c) => !readSet.has(c.path)) ?? chapters.find((c) => !readSet.has(c.path));
  }

  if (inProgress) {
    readBtn.href = readerUrl(pluginId, novelPath, novel.name, novel.cover, inProgress, historyEntry, contentType, fromParam, libReturnParams);
    readBtn.textContent = `Продовжити читання: ${inProgress.name}`;
  } else if (nextUnread) {
    readBtn.href = readerUrl(pluginId, novelPath, novel.name, novel.cover, nextUnread, undefined, undefined, fromParam, libReturnParams);
    readBtn.textContent = readSet.size ? `Продовжити читання: ${nextUnread.name}` : `Почати читання: ${nextUnread.name}`;
  } else {
    const first = chapters[0];
    readBtn.href = readerUrl(pluginId, novelPath, novel.name, novel.cover, first, undefined, undefined, fromParam, libReturnParams);
    readBtn.textContent = `Почати читання: ${first.name}`;
  }
}

/** Кнопка "Додати"/"У бібліотеці" + показ/приховування кнопки "Перенести". */
// Актуальні дані для кнопки "Додати"/"У бібліотеці" — оновлюються при
// кожному виклику setupLibraryButton (спершу миттєво з даних бібліотеки/
// спільного кешу, потім ще раз зі свіжих живих даних), а сам клік-обробник
// підключається лише ОДИН раз і завжди читає звідси найсвіжіше — інакше
// повторний виклик додав би другий обробник і клік спрацьовував би двічі.
let libraryBtnState: {
  novelId: string;
  name: string;
  cover: string | undefined;
  pluginId: string;
  path: string;
  contentType: 'novel' | 'manga' | 'anime';
  categoryIds: string[];
} | null = null;
let libraryBtnWired = false;

function setupLibraryButton(
  novelId: string,
  name: string,
  cover: string | undefined,
  inLibrary: boolean,
  pluginId: string,
  path: string,
  contentType: 'novel' | 'manga' | 'anime' = 'novel',
  categoryIds: string[] = [],
) {
  const libraryBtn = document.getElementById('library-btn') as HTMLButtonElement;
  const migrateBtn = document.getElementById('migrate-btn') as HTMLButtonElement;

  libraryBtnState = { novelId, name, cover, pluginId, path, contentType, categoryIds };

  function paintLibraryBtn(inLib: boolean) {
    libraryBtn.classList.toggle('active', inLib);
    (libraryBtn.querySelector('span') as HTMLElement).textContent = inLib ? 'У бібліотеці' : 'Додати';
    migrateBtn.style.display = inLib ? 'flex' : 'none';
  }
  paintLibraryBtn(inLibrary);

  if (libraryBtnWired) return;
  libraryBtnWired = true;

  // Довге утримання на кнопці — ЛИШЕ коли твір уже в бібліотеці — відкриває
  // вибір/зміну категорії. Короткий клік лишає стару поведінку (вилучити),
  // окрім того, що додавання твору, якого ще нема в бібліотеці, тепер
  // спершу відкриває меню вибору категорії (див. openLibraryCategoryOverlay).
  // КРИТИЧНО: після довгого натискання браузер усе одно шле звичайний
  // "click" по відпусканню пальця — якщо не погасити саме цей клік, він
  // одразу ж додатково відкриє/закриє те, що вже зробило довге утримання.
  let pressTimer: number | undefined;
  let moved = false;
  let longPressFired = false;

  libraryBtn.addEventListener('pointerdown', (e: PointerEvent) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    if (!libraryBtn.classList.contains('active')) return;
    moved = false;
    pressTimer = window.setTimeout(() => {
      if (!moved) {
        longPressFired = true;
        void openLibraryCategoryOverlay('change');
      }
    }, 450);
  });
  libraryBtn.addEventListener('pointermove', (e: PointerEvent) => {
    if (Math.abs(e.movementX) + Math.abs(e.movementY) > 6) moved = true;
  });
  const clearPressTimer = () => {
    if (pressTimer) window.clearTimeout(pressTimer);
  };
  libraryBtn.addEventListener('pointerup', clearPressTimer);
  libraryBtn.addEventListener('pointerleave', clearPressTimer);
  libraryBtn.addEventListener('contextmenu', (e) => e.preventDefault());

  libraryBtn.addEventListener('click', async (e) => {
    if (longPressFired) {
      longPressFired = false;
      e.preventDefault();
      return;
    }
    if (!libraryBtnState) return;
    if (libraryBtn.classList.contains('active')) {
      const { novelId } = libraryBtnState;
      try {
        await api.removeFromLibrary(novelId);
        paintLibraryBtn(false);
        toast('Видалено з бібліотеки');
      } catch (err) {
        toast((err as Error).message);
      }
    } else {
      await openLibraryCategoryOverlay('add');
    }
  });
}

// ---------------- меню вибору/зміни категорії бібліотеки (сторінка твору) ----------------

let libraryCategoryMode: 'add' | 'change' = 'add';
let libraryCategoryWired = false;

/**
 * Показує меню вибору категорії:
 *  - mode "add" — твору ще нема в бібліотеці, треба обрати куди додати
 *    (нічого не обрано ⇒ "Типово");
 *  - mode "change" — твір уже в бібліотеці (відкривається довгим
 *    утриманням на кнопці), тут обирають нову категорію замість поточної.
 * Якщо в цій власній бібліотеці (за типом контенту) ще нема жодної
 * категорії — показувати порожнє меню сенсу нема: у режимі "add" одразу
 * додаємо в "Типово", а в режимі "change" підказуємо спершу створити
 * категорію в бібліотеці.
 */
async function openLibraryCategoryOverlay(mode: 'add' | 'change') {
  if (!libraryBtnState) return;
  const { contentType, categoryIds } = libraryBtnState;

  let categories: Category[] = [];
  try {
    categories = await api.categories(contentType);
  } catch {
    categories = [];
  }

  if (!categories.length) {
    if (mode === 'add') await commitAddToLibrary(undefined);
    else toast('Спершу створіть категорію в бібліотеці');
    return;
  }

  const overlay = document.getElementById('library-category-overlay') as HTMLElement;
  const title = document.getElementById('library-category-title') as HTMLElement;
  const list = document.getElementById('library-category-list') as HTMLElement;
  const applyBtn = document.getElementById('library-category-apply') as HTMLButtonElement;
  const closeBtn = document.getElementById('library-category-close') as HTMLButtonElement;

  libraryCategoryMode = mode;
  title.textContent = mode === 'add' ? 'Додати до бібліотеки' : 'Змінити категорію';
  applyBtn.textContent = mode === 'add' ? 'Додати' : 'Зберегти';

  const preselected = mode === 'change' ? categoryIds[0] : undefined;

  list.innerHTML = '';
  const uncatRow = document.createElement('label');
  uncatRow.className = 'assign-cat-row';
  const uncatRadio = document.createElement('input');
  uncatRadio.type = 'radio';
  uncatRadio.name = 'library-category-radio';
  uncatRadio.value = '';
  uncatRadio.checked = !preselected;
  const uncatSpan = document.createElement('span');
  uncatSpan.textContent = 'Типово';
  uncatRow.appendChild(uncatRadio);
  uncatRow.appendChild(uncatSpan);
  list.appendChild(uncatRow);

  for (const c of categories) {
    const row = document.createElement('label');
    row.className = 'assign-cat-row';
    const radio = document.createElement('input');
    radio.type = 'radio';
    radio.name = 'library-category-radio';
    radio.value = c.id;
    radio.checked = preselected === c.id;
    const span = document.createElement('span');
    span.textContent = c.name;
    row.appendChild(radio);
    row.appendChild(span);
    list.appendChild(row);
  }

  overlay.style.display = 'flex';

  if (!libraryCategoryWired) {
    libraryCategoryWired = true;
    closeBtn.addEventListener('click', () => (overlay.style.display = 'none'));
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) overlay.style.display = 'none';
    });
    applyBtn.addEventListener('click', async () => {
      const checked = list.querySelector<HTMLInputElement>('input[name="library-category-radio"]:checked');
      const chosenId = checked?.value || undefined;
      overlay.style.display = 'none';
      if (libraryCategoryMode === 'add') await commitAddToLibrary(chosenId);
      else await commitCategoryChange(chosenId);
    });
  }
}

/** Додає твір до бібліотеки й (якщо обрано) одразу призначає йому категорію. */
async function commitAddToLibrary(categoryId: string | undefined) {
  if (!libraryBtnState) return;
  const { pluginId, path, name, cover, novelId } = libraryBtnState;
  const libraryBtn = document.getElementById('library-btn') as HTMLButtonElement;
  const migrateBtn = document.getElementById('migrate-btn') as HTMLButtonElement;
  try {
    await api.addToLibrary({ pluginId, path, name, cover });
    if (categoryId) {
      try {
        await api.assignLibraryCategories([novelId], [categoryId], []);
      } catch (err) {
        toast((err as Error).message);
      }
    }
    if (libraryBtnState) libraryBtnState.categoryIds = categoryId ? [categoryId] : [];
    libraryBtn.classList.add('active');
    (libraryBtn.querySelector('span') as HTMLElement).textContent = 'У бібліотеці';
    migrateBtn.style.display = 'flex';
    toast('Додано до бібліотеки');
  } catch (err) {
    toast((err as Error).message);
  }
}

/** Змінює категорію твору, який уже в бібліотеці, — замінює попередню на нову (або на "Типово"). */
async function commitCategoryChange(categoryId: string | undefined) {
  if (!libraryBtnState) return;
  const { novelId, categoryIds } = libraryBtnState;
  const add = categoryId ? [categoryId] : [];
  const remove = categoryIds.filter((id) => id !== categoryId);
  try {
    await api.assignLibraryCategories([novelId], add, remove);
    libraryBtnState.categoryIds = categoryId ? [categoryId] : [];
    toast('Категорію змінено');
  } catch (err) {
    toast((err as Error).message);
  }
}


type SortOrder = 'asc' | 'desc';
type ChapterStatus = 'read' | 'progress' | 'unread';

/**
 * Дії "Рекомендувати" (заглушка) / "Завантажити" (епаб-експорт, лише для
 * novel — вмикається самим setupChaptersSection нижче) / "..." меню
 * (Поділитися / Оновити). Ідентичний набір кнопок дублюється у ДВОХ місцях
 * — верхній прозорій панелі (завжди видима) і липкій суцільній панелі (яка
 * з'являється при скролі, коли оригінальна ховається) — тому й підключаємо
 * однаковою логікою до обох наборів id одразу.
 */
const headerActionsWired = new Set<string>();

/**
 * Дані поточного твору для кнопки "Рекомендувати" — оновлюються і після
 * швидкого (кешованого), і після живого показу сторінки (initNovel нижче),
 * а сам обробник кліку читає їх у момент кліку (а не на момент підключення
 * обробника, яке відбувається рівно один раз через headerActionsWired).
 */
const recommendState: { pluginId: string; novelPath: string; name: string; cover?: string; chapterCount: number } = {
  pluginId: '',
  novelPath: '',
  name: '',
  cover: undefined,
  chapterCount: 0,
};

/** Модалка кнопки "Рекомендувати": список друзів з дією рекомендувати конкретному другові цей твір. */
async function openRecommendModal() {
  const { pluginId, novelPath, name, cover, chapterCount } = recommendState;
  if (!pluginId || !novelPath) return;

  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay-center';
  overlay.innerHTML = `
    <div class="center-modal">
      <h3 class="modal-title">Рекомендувати «${escapeHtml(name)}»</h3>
      <div id="recommend-modal-body"><p class="recommend-modal-empty">Завантаження…</p></div>
      <div class="modal-actions">
        <button type="button" id="recommend-modal-close" class="btn">Закрити</button>
      </div>
    </div>`;
  document.body.appendChild(overlay);

  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') close();
  };
  const close = () => {
    document.removeEventListener('keydown', onKey);
    overlay.remove();
  };
  document.addEventListener('keydown', onKey);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });
  (overlay.querySelector('#recommend-modal-close') as HTMLButtonElement).addEventListener('click', close);

  const body = overlay.querySelector('#recommend-modal-body') as HTMLElement;
  let targets: RecommendTarget[];
  try {
    targets = (await api.recommendTargets(pluginId, novelPath)).targets;
  } catch (err) {
    body.innerHTML = `<p class="recommend-modal-empty">${escapeHtml((err as Error).message)}</p>`;
    return;
  }

  if (!targets.length) {
    body.innerHTML = '<p class="recommend-modal-empty">У вас поки немає друзів — додайте їх у розділі «Більше → Друзі».</p>';
    return;
  }

  body.innerHTML = '<div class="recommend-modal-list"></div>';
  const list = body.querySelector('.recommend-modal-list') as HTMLElement;
  for (const target of targets) {
    const row = document.createElement('div');
    row.className = 'recommend-modal-row';
    row.innerHTML = `
      <div class="friend-avatar">${escapeHtml(target.username.slice(0, 2).toUpperCase())}</div>
      <span class="recommend-username">${escapeHtml(target.username)}</span>`;

    const btn = document.createElement('button');
    btn.type = 'button';
    if (target.status === 'seen') {
      btn.className = 'btn-sent';
      btn.textContent = 'Вже бачив';
      btn.disabled = true;
    } else if (target.status === 'sent') {
      btn.className = 'btn-sent';
      btn.textContent = 'Рекомендовано';
      btn.disabled = true;
    } else {
      btn.className = 'btn-accent';
      btn.textContent = 'Рекомендувати';
      btn.addEventListener('click', async () => {
        btn.disabled = true;
        btn.textContent = 'Надсилаю…';
        try {
          await api.sendRecommendation({ toUsername: target.username, pluginId, path: novelPath, name, cover, chapterCount });
          btn.className = 'btn-sent';
          btn.textContent = 'Рекомендовано';
        } catch (err) {
          btn.disabled = false;
          btn.textContent = 'Рекомендувати';
          toast((err as Error).message);
        }
      });
    }
    row.appendChild(btn);
    list.appendChild(row);
  }
}

function setupHeaderActions(idPrefix: 'novel' | 'novel-sticky') {
  // Сторінка тепер може малюватись двічі (спершу зі спільного кешу, потім
  // зі свіжих живих даних) — обробники кнопок мають підключитись рівно один раз.
  if (headerActionsWired.has(idPrefix)) return;
  headerActionsWired.add(idPrefix);

  const menuBtn = document.getElementById(`${idPrefix}-menu-btn`) as HTMLButtonElement;
  const menuDropdown = document.getElementById(`${idPrefix}-menu-dropdown`) as HTMLElement;
  const menuShare = document.getElementById(`${idPrefix}-menu-share`) as HTMLButtonElement;
  const menuRefresh = document.getElementById(`${idPrefix}-menu-refresh`) as HTMLButtonElement;
  const recommendBtn = document.getElementById(`${idPrefix}-recommend-btn`) as HTMLButtonElement;

  menuBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    menuDropdown.style.display = menuDropdown.style.display === 'none' ? 'flex' : 'none';
  });
  document.addEventListener('click', (e) => {
    if (menuDropdown.style.display !== 'none' && !menuDropdown.contains(e.target as Node) && e.target !== menuBtn) {
      menuDropdown.style.display = 'none';
    }
  });

  menuShare.addEventListener('click', async () => {
    menuDropdown.style.display = 'none';
    const shareTitle = recommendState.name || document.title;
    // коротке посилання /<джерело>/<слаг> (див. getShareUrl) — не залежить від того, чи встигла адреса підмінитись
    const shareUrl = await getShareUrl(recommendState.pluginId, recommendState.novelPath, shareTitle);
    if (navigator.share) {
      try {
        await navigator.share({ title: shareTitle, url: shareUrl });
      } catch {
        /* користувач скасував вибір застосунку в системному меню — нічого не робимо */
      }
      return;
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast('Посилання скопійовано');
    } catch {
      toast('Не вдалося скопіювати посилання');
    }
  });

  menuRefresh.addEventListener('click', () => {
    menuDropdown.style.display = 'none';
    // Сторінка й так завжди читає дані наживо (без клієнтського кешування) —
    // "Оновити" просто перезапускає це начисто, гарантовано скидаючи будь-
    // який локальний JS-стан сторінки (вибір розділів, розкриті описи тощо).
    toast('Оновлюю дані твору…');
    location.reload();
  });

  recommendBtn.addEventListener('click', () => {
    openRecommendModal();
  });
}

/** З'ява/зникнення липкої (суцільної) панелі — коли оригінальна прозора верхня панель іде за межі екрана. */
let stickyBarWired = false;

function setupStickyBar() {
  if (stickyBarWired) return;
  stickyBarWired = true;

  const stickyBar = document.getElementById('novel-sticky-bar') as HTMLElement;
  const heroBar = document.querySelector('.novel-hero-bar') as HTMLElement | null;

  if (heroBar && 'IntersectionObserver' in window) {
    const observer = new IntersectionObserver(
      ([entry]) => stickyBar.classList.toggle('visible', !entry.isIntersecting),
      { threshold: 0 },
    );
    observer.observe(heroBar);
  }

  setupHeaderActions('novel');
  setupHeaderActions('novel-sticky');
}

/**
 * Сортування (нові⇄старі) + приховування прочитаних + режим вибору розділів
 * з масовими діями (позначити прочитаними/непрочитаними, прочитати до початку).
 */
/**
 * Повертається setupChaptersSection — дозволяє ПОВТОРНО намалювати список
 * (наприклад, зі свіжими даними, коли підійшов живий запит після
 * попереднього швидкого показу зі спільного кешу), не навішуючи обробники
 * подій вдруге (кнопки сортування/вибору/epub і т.д. підключаються рівно
 * один раз, всередині setupChaptersSection).
 */
interface ChaptersSectionHandle {
  update(novel: SourceNovel, chapters: ChapterItem[], readSet: Set<string>): void;
}

function setupChaptersSection(
  pluginId: string,
  novelPath: string,
  novel: SourceNovel,
  chapters: ChapterItem[],
  readSet: Set<string>,
  getHistoryEntry: () => HistoryItem | undefined,
  filterBtn: HTMLElement,
  list: HTMLElement,
  chaptersCountEl: HTMLElement,
  contentType: 'novel' | 'manga' | 'anime',
  fromParam: string | null,
  libReturnParams: URLSearchParams,
  libraryId: string | undefined,
  librarySortOrder: 'asc' | 'desc' | undefined,
): ChaptersSectionHandle {
  const SORT_KEY = `yomukoma:chapters-sort:${pluginId}`;

  function loadSortOrder(): SortOrder {
    // Для творів у бібліотеці порядок сортування запам'ятовується персонально
    // для кожного твору на сервері — це має пріоритет над типовим (спільним
    // для джерела) значенням з localStorage.
    if (librarySortOrder === 'asc' || librarySortOrder === 'desc') return librarySortOrder;
    try {
      const v = localStorage.getItem(SORT_KEY);
      if (v === 'asc' || v === 'desc') return v;
    } catch {
      /* localStorage недоступний — використовуємо типове значення */
    }
    return 'asc';
  }
  function saveSortOrder(v: SortOrder) {
    try {
      localStorage.setItem(SORT_KEY, v);
    } catch {
      /* ігноруємо */
    }
    if (libraryId) {
      api.updateLibrarySortOrder(libraryId, v).catch(() => {
        /* не критично — просто не запам'ятається персонально для твору */
      });
    }
  }

  let sortOrder: SortOrder = loadSortOrder();
  let hideRead = false;
  // Заголовки й приховування томів — лише для smakolykytl (єдине джерело з назвами томів у даних розділів).
  // Рахується щоразу з ПОТОЧНОГО списку розділів: спочатку список може прийти зі старого кешу (без даних про томи),
  // а живі дані з'являються пізніше через update() — тож це не const.
  const hasVolumes = () => pluginId === 'smakolykytl' && chapters.some((c) => !!c.volumeKey);
  // Приховані томи діють лише поки відкрита сторінка — НЕ зберігаються (ні в кеші, ні на сервері).
  const hiddenVolumes = new Set<string>();
  let selectionMode = false;
  const selectedPaths = new Set<string>();

  // --- Перемикач "Озвучення/Субтитри" (лише для contentType==='anime', коли
  // novel.groups містить 2+ групи — інакше пігулки не показуємо взагалі,
  // а список розділів просто не фільтрується за group). ---
  const groupPickerEl = document.getElementById('group-picker') as HTMLElement;
  let selectedGroup: string | null = null;

  function renderGroupPicker() {
    const groups = novel.groups ?? [];
    if (groups.length < 2) {
      groupPickerEl.style.display = 'none';
      groupPickerEl.innerHTML = '';
      selectedGroup = null;
      return;
    }
    // Якщо вже обрана група й досі існує серед нових — лишаємо вибір,
    // інакше (перший рендер чи групи повністю змінились) — перша в списку
    // (плагін сам сортує так, щоб озвучення йшло перед субтитрами).
    if (!selectedGroup || !groups.some((g) => g.id === selectedGroup)) {
      selectedGroup = groups[0].id;
    }
    groupPickerEl.style.display = 'flex';
    groupPickerEl.innerHTML = '';
    for (const g of groups) {
      const pill = document.createElement('button');
      pill.type = 'button';
      pill.className = 'group-pill' + (g.id === selectedGroup ? ' active' : '');
      pill.textContent = g.label;
      pill.addEventListener('click', () => {
        if (selectedGroup === g.id) return;
        selectedGroup = g.id;
        renderGroupPicker();
        renderChapterList();
      });
      groupPickerEl.appendChild(pill);
    }
  }

  const content = document.querySelector('.content') as HTMLElement | null;
  const selectBar = document.getElementById('chapters-select-bar') as HTMLElement;
  const csbCancel = document.getElementById('csb-cancel') as HTMLElement;
  const csbCount = document.getElementById('csb-count') as HTMLElement;
  const btnAll = document.getElementById('csb-btn-all') as HTMLButtonElement;
  const btnRange = document.getElementById('csb-btn-range') as HTMLButtonElement;
  const btn1 = document.getElementById('csb-btn-1') as HTMLButtonElement;
  const btn2 = document.getElementById('csb-btn-2') as HTMLButtonElement;
  const btn3 = document.getElementById('csb-btn-3') as HTMLButtonElement;

  const sortOverlay = document.getElementById('chapters-sort-overlay') as HTMLElement;
  const sortClose = document.getElementById('chapters-sort-close') as HTMLElement;
  const sortRowDesc = document.getElementById('sort-row-desc') as HTMLElement;
  const sortRowAsc = document.getElementById('sort-row-asc') as HTMLElement;
  const hideReadCheckbox = document.getElementById('hide-read-checkbox') as HTMLInputElement;

  function chapterStatus(ch: ChapterItem): ChapterStatus {
    if (readSet.has(ch.path)) return 'read';
    const h = getHistoryEntry();
    if (h && h.chapterPath === ch.path && h.progress > 0 && h.progress < 100) return 'progress';
    return 'unread';
  }

  function orderedChapters(): ChapterItem[] {
    const arr = chapters.slice();
    if (sortOrder === 'desc') arr.reverse();
    return arr;
  }

  function visibleChapters(): ChapterItem[] {
    let arr = orderedChapters();
    if (selectedGroup) arr = arr.filter((ch) => ch.group === selectedGroup);
    if (hiddenVolumes.size) arr = arr.filter((ch) => !ch.volumeKey || !hiddenVolumes.has(ch.volumeKey));
    return hideRead ? arr.filter((ch) => !readSet.has(ch.path)) : arr;
  }

  /** Текст рядка-заголовка тому у списку розділів; порожній — заголовок не потрібен. */
  function volumeHeadingLabel(ch: ChapterItem): string {
    const hasNo = ch.volumeNumber !== undefined;
    // Старі кеші не мають volumeShown: тоді volumeNumber був заданий лише коли "Том N" писався в кнопках.
    const shownInButtons = ch.volumeShown ?? hasNo;
    if (hasNo && ch.volumeName) return `Том ${ch.volumeNumber}: ${ch.volumeName}`;
    if (ch.volumeName) return ch.volumeName;
    if (hasNo && !shownInButtons) return `Том ${ch.volumeNumber}`;
    return '';
  }

  function renderChapterList() {
    list.innerHTML = '';
    let prevVolume: string | undefined;
    const volumesOn = hasVolumes();
    for (const ch of visibleChapters()) {
      // Назва тому — один рядок-заголовок у ТОМУ Ж списку (не розбиваючи його): над першим розділом
      // тому при "від старих до нових" і над останнім при "від нових до старих" — тобто завжди там,
      // де в поточному порядку починається блок розділів цього тому. Іменований том: "Том N: Назва"
      // (слово "Том N" не ховаємо). Том без назви, у якого "Том N" сховано в кнопках через наскрізну
      // нумерацію розділів, — окремий рядок "Том N".
      if (volumesOn && ch.volumeKey && ch.volumeKey !== prevVolume) {
        const label = volumeHeadingLabel(ch);
        if (label) {
          const head = document.createElement('div');
          head.className = 'chvolume';
          head.textContent = label;
          list.appendChild(head);
        }
      }
      prevVolume = ch.volumeKey;
      const status = chapterStatus(ch);
      const a = document.createElement('a');
      a.className = 'chrow';
      if (status === 'read') a.classList.add('read');
      if (selectionMode && selectedPaths.has(ch.path)) a.classList.add('selected');
      const h = getHistoryEntry();
      a.href = readerUrl(pluginId, novelPath, novel.name, novel.cover, ch, h, contentType, fromParam, libReturnParams);
      const progressLabel =
        status === 'progress' && h
          ? contentType === 'manga' && h.totalPages
            ? `стор. ${h.page}/${h.totalPages}`
            : `${h.progress}%`
          : '';
      const dateHtml =
        escapeHtml(ch.releaseTime || '') + (progressLabel ? ` <span class="ch-progress">• ${escapeHtml(progressLabel)}</span>` : '');
      a.innerHTML = `
        <span class="chdot${status !== 'read' ? ' unread' : ''}"></span>
        <div class="chmeta">
          <p class="chtitle">${escapeHtml(ch.name)}</p>
          <p class="chdate">${dateHtml}</p>
        </div>`;
      attachChapterRowInteractions(a, ch.path);
      list.appendChild(a);
    }
  }

  /**
   * Вибір розділів — довгим утриманням (як картки в бібліотеці), а не
   * окремою кнопкою: довге натискання вмикає режим вибору й одразу обирає
   * цей розділ; звичайний тап у режимі вибору перемикає вибір; звичайний тап
   * поза режимом вибору відкриває розділ як завжди (через href). КРИТИЧНО:
   * після довгого натискання браузер усе одно шле звичайний "click" по
   * відпусканню пальця — якщо не погасити саме цей клік, він одразу ж знову
   * відкриє розділ (чи зніме щойно поставлений вибір).
   */
  function attachChapterRowInteractions(el: HTMLAnchorElement, chPath: string) {
    // Гість: без виділення розділів — лише звичайний перехід за href.
    if (guestMode) return;
    let timer: number | undefined;
    let moved = false;
    let longPressFired = false;

    el.addEventListener('pointerdown', (e: PointerEvent) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      moved = false;
      timer = window.setTimeout(() => {
        if (!moved && !selectionMode) {
          longPressFired = true;
          selectionMode = true;
          selectedPaths.add(chPath);
          renderChapterList();
          updateSelectionBar();
        }
      }, 450);
    });
    el.addEventListener('pointermove', (e: PointerEvent) => {
      if (Math.abs(e.movementX) + Math.abs(e.movementY) > 6) moved = true;
    });
    const clearTimer = () => {
      if (timer) window.clearTimeout(timer);
    };
    el.addEventListener('pointerup', clearTimer);
    el.addEventListener('pointerleave', clearTimer);
    el.addEventListener('contextmenu', (e) => e.preventDefault());

    el.addEventListener('click', (e) => {
      if (longPressFired) {
        longPressFired = false;
        e.preventDefault();
        return;
      }
      if (!selectionMode) return; // звичайна навігація за href
      e.preventDefault();
      toggleSelect(chPath);
    });
  }

  function toggleSelect(chPath: string) {
    if (selectedPaths.has(chPath)) selectedPaths.delete(chPath);
    else selectedPaths.add(chPath);
    // Зняли останній обраний розділ — виходимо з режиму вибору.
    if (selectedPaths.size === 0) {
      exitSelection();
      return;
    }
    renderChapterList();
    updateSelectionBar();
  }

  function updateSelectionBar() {
    const count = selectedPaths.size;
    const show = selectionMode && count > 0;
    selectBar.style.display = show ? 'flex' : 'none';
    content?.classList.toggle('select-bar-open', show);
    if (!show) return;

    csbCount.textContent = `Обрано: ${count}`;

    // "Обрати всі" — завжди доступна, поки відкрита панель вибору.
    btnAll.style.display = 'flex';
    // "Обрати діапазон між двома" — лише коли обрано рівно два розділи.
    btnRange.style.display = count === 2 ? 'flex' : 'none';

    let visibleBtns: number[];
    if (count === 1) {
      const status = chapterStatus(chapters.find((c) => c.path === [...selectedPaths][0])!);
      if (status === 'progress') visibleBtns = [1, 2, 3];
      else if (status === 'unread') visibleBtns = [1, 3];
      else visibleBtns = [2, 3];
    } else {
      visibleBtns = [1, 2];
    }
    btn1.style.display = visibleBtns.includes(1) ? 'flex' : 'none';
    btn2.style.display = visibleBtns.includes(2) ? 'flex' : 'none';
    btn3.style.display = visibleBtns.includes(3) ? 'flex' : 'none';
  }

  function exitSelection() {
    selectionMode = false;
    selectedPaths.clear();
    selectBar.style.display = 'none';
    content?.classList.remove('select-bar-open');
    renderChapterList();
  }

  csbCancel.addEventListener('click', exitSelection);

  // "Обрати всі" — увесь список розділів (незалежно від поточного сортування/фільтра).
  btnAll.addEventListener('click', () => {
    for (const ch of chapters) selectedPaths.add(ch.path);
    renderChapterList();
    updateSelectionBar();
  });

  // "Обрати діапазон" — між двома вже обраними розділами (за позицією в
  // каноничному, відсортованому за номером списку `chapters`, а не в порядку
  // поточного відображення desc/asc) включно з ними самими.
  btnRange.addEventListener('click', () => {
    const [pathA, pathB] = [...selectedPaths];
    const idxA = chapters.findIndex((c) => c.path === pathA);
    const idxB = chapters.findIndex((c) => c.path === pathB);
    if (idxA < 0 || idxB < 0) return;
    const [from, to] = idxA < idxB ? [idxA, idxB] : [idxB, idxA];
    for (let i = from; i <= to; i++) selectedPaths.add(chapters[i].path);
    renderChapterList();
    updateSelectionBar();
  });

  // 1. Позначити всі обрані розділи прочитаними.
  btn1.addEventListener('click', async () => {
    const paths = [...selectedPaths];
    try {
      await api.markChaptersRead({ pluginId, novelPath, chapters: paths.map((p) => ({ path: p })) });
      paths.forEach((p) => readSet.add(p));
      toast('Позначено прочитаними');
      exitSelection();
    } catch (err) {
      toast((err as Error).message);
    }
  });

  // 2. Позначити всі обрані розділи непрочитаними (розділу з прогресом — скинути прогрес).
  btn2.addEventListener('click', async () => {
    const paths = [...selectedPaths];
    try {
      await api.markChaptersUnread({ pluginId, novelPath, chapters: paths.map((p) => ({ path: p })) });
      paths.forEach((p) => readSet.delete(p));
      const h = getHistoryEntry();
      // Скидаємо не лише progress, а й page/totalPages/positionSeconds — так само,
      // як це вже робить сервер (db.resetChapterProgress). Інакше локальний
      // закешований historyEntry в пам'яті лишається зі старою сторінкою, і
      // chapterUrl() (яка бере саме h.page для манги) все одно відкриє розділ
      // на старому місці, навіть якщо на сервері прогрес уже скинуто.
      if (h && paths.includes(h.chapterPath)) {
        h.progress = 0;
        h.page = undefined;
        h.totalPages = undefined;
        h.positionSeconds = undefined;
        h.durationSeconds = undefined;
      }
      toast('Позначено непрочитаними');
      exitSelection();
    } catch (err) {
      toast((err as Error).message);
    }
  });

  // 3. Позначити прочитаними всі розділи від обраного до самого початку твору.
  btn3.addEventListener('click', async () => {
    const onlyPath = [...selectedPaths][0];
    const idx = chapters.findIndex((c) => c.path === onlyPath);
    if (idx < 0) return;
    const rangePaths = chapters.slice(0, idx + 1).map((c) => c.path);
    try {
      await api.markChaptersRead({ pluginId, novelPath, chapters: rangePaths.map((p) => ({ path: p })) });
      rangePaths.forEach((p) => readSet.add(p));
      toast('Позначено прочитаними до початку');
      exitSelection();
    } catch (err) {
      toast((err as Error).message);
    }
  });

  // --- сортування / приховування прочитаних ---
  function paintSortRows() {
    sortRowDesc.classList.toggle('active', sortOrder === 'desc');
    sortRowAsc.classList.toggle('active', sortOrder === 'asc');
  }

  // --- приховування окремих томів: вибір зі списку (як вибір жанрів у фільтрах каталогу), без збереження ---
  const volumesBlock = document.getElementById('chapters-volumes-block') as HTMLElement | null;
  const volumesBtnText = document.getElementById('chapters-volumes-text') as HTMLElement | null;
  const volumesOverlay = document.getElementById('volumes-overlay') as HTMLElement | null;
  const volumesList = document.getElementById('volumes-list') as HTMLElement | null;
  const volumesApply = document.getElementById('volumes-apply') as HTMLButtonElement | null;
  const volumesClose = document.getElementById('volumes-close') as HTMLElement | null;
  const CHECK_SVG =
    '<svg class="check" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>';

  // Усі томи твору — у порядку появи в списку розділів (за зростанням), незалежно від того, чи має том номер/назву.
  function volumeOptions(): Array<{ key: string; short: string; label: string }> {
    const seen = new Map<string, ChapterItem>();
    for (const ch of chapters) {
      if (ch.volumeKey && !seen.has(ch.volumeKey)) seen.set(ch.volumeKey, ch);
    }
    return [...seen.entries()].map(([key, ch]) => {
      const short = ch.volumeNumber !== undefined ? `Том ${ch.volumeNumber}` : ch.volumeName || 'Без тому';
      // Усі томи (і без назви теж) — "Том N"; з назвою — "Том N: Назва".
      const label = ch.volumeNumber !== undefined && ch.volumeName ? `Том ${ch.volumeNumber}: ${ch.volumeName}` : short;
      return { key, short, label };
    });
  }

  function paintVolumesButton() {
    if (!volumesBlock || !volumesBtnText) return;
    // Меню приховування томів — лише коли томів кілька: приховувати єдиний том немає сенсу.
    const on = hasVolumes() && volumeOptions().length > 1;
    volumesBlock.style.display = on ? '' : 'none';
    if (!on) return;
    const opts = volumeOptions().filter((o) => hiddenVolumes.has(o.key));
    if (!opts.length) volumesBtnText.textContent = 'Не обрано';
    else if (opts.length <= 2) volumesBtnText.textContent = opts.map((o) => o.short).join(', ');
    else volumesBtnText.textContent = `Обрано: ${opts.length}`;
  }

  function openVolumesOverlay() {
    if (!volumesOverlay || !volumesList || !volumesApply || !volumesClose) return;
    const temp = new Set(hiddenVolumes);
    const renderList = () => {
      volumesList.innerHTML = '';
      for (const o of volumeOptions()) {
        const sel = temp.has(o.key);
        const r = document.createElement('div');
        r.className = 'migrate-plugin-row picker-option' + (sel ? ' selected' : '');
        r.innerHTML = `<span>${escapeHtml(o.label)}</span>${sel ? CHECK_SVG : ''}`;
        r.addEventListener('click', () => {
          if (temp.has(o.key)) temp.delete(o.key);
          else temp.add(o.key);
          renderList();
        });
        volumesList.appendChild(r);
      }
    };
    renderList();
    volumesApply.onclick = () => {
      hiddenVolumes.clear();
      temp.forEach((n) => hiddenVolumes.add(n));
      paintVolumesButton();
      volumesOverlay.style.display = 'none';
      renderChapterList();
    };
    volumesClose.onclick = () => (volumesOverlay.style.display = 'none');
    volumesOverlay.onclick = (e) => {
      if (e.target === volumesOverlay) volumesOverlay.style.display = 'none';
    };
    volumesOverlay.style.display = 'flex';
  }
  document.getElementById('chapters-volumes-btn')?.addEventListener('click', openVolumesOverlay);

  filterBtn.addEventListener('click', () => {
    paintSortRows();
    paintVolumesButton();
    hideReadCheckbox.checked = hideRead;
    sortOverlay.style.display = 'flex';
  });
  sortClose.addEventListener('click', () => (sortOverlay.style.display = 'none'));
  sortOverlay.addEventListener('click', (e) => {
    if (e.target === sortOverlay) sortOverlay.style.display = 'none';
  });
  sortRowDesc.addEventListener('click', () => {
    sortOrder = 'desc';
    saveSortOrder(sortOrder);
    paintSortRows();
    renderChapterList();
  });
  sortRowAsc.addEventListener('click', () => {
    sortOrder = 'asc';
    saveSortOrder(sortOrder);
    paintSortRows();
    renderChapterList();
  });
  hideReadCheckbox.addEventListener('change', () => {
    hideRead = hideReadCheckbox.checked;
    renderChapterList();
  });

  // --- експорт у Epub (лише для джерел типу "Novel") ---
  // Кнопка "Завантажити" дубльована у верхній і липкій панелях (як і
  // рекомендувати/меню, setupStickyBar вище) — керуємо обома одразу.
  const heroDownloadBtn = document.getElementById('novel-download-btn') as HTMLElement;
  const stickyDownloadBtn = document.getElementById('novel-sticky-download-btn') as HTMLElement;
  if (contentType === 'novel') {
    heroDownloadBtn.style.display = 'flex';
    stickyDownloadBtn.style.display = 'flex';

    const epubOverlay = document.getElementById('epub-export-overlay') as HTMLElement;
    const epubClose = document.getElementById('epub-export-close') as HTMLElement;
    const epubCancel = document.getElementById('epub-cancel-btn') as HTMLButtonElement;
    const epubConfirm = document.getElementById('epub-confirm-btn') as HTMLButtonElement;
    const epubFilename = document.getElementById('epub-filename-input') as HTMLInputElement;
    const epubAllChaptersCheckbox = document.getElementById('epub-all-chapters-checkbox') as HTMLInputElement;
    const epubRangeRow = document.getElementById('epub-range-row') as HTMLElement;
    const epubFrom = document.getElementById('epub-from-input') as HTMLInputElement;
    const epubTo = document.getElementById('epub-to-input') as HTMLInputElement;
    const epubSiteTheme = document.getElementById('epub-site-theme-checkbox') as HTMLInputElement;
    const epubCustomCss = document.getElementById('epub-custom-css-checkbox') as HTMLInputElement;
    const epubCustomCssTextarea = document.getElementById('epub-custom-css-textarea') as HTMLTextAreaElement;

    function closeEpubOverlay() {
      epubOverlay.style.display = 'none';
    }

    function openEpubOverlay() {
      epubFilename.value = novel.name;
      epubAllChaptersCheckbox.checked = true;
      epubRangeRow.style.display = 'none';
      epubFrom.value = '1';
      epubTo.value = String(chapters.length);
      epubFrom.min = '1';
      epubFrom.max = String(chapters.length);
      epubTo.min = '1';
      epubTo.max = String(chapters.length);
      epubSiteTheme.checked = false;
      epubCustomCss.checked = false;
      epubCustomCssTextarea.style.display = 'none';
      epubOverlay.style.display = 'flex';
    }

    heroDownloadBtn.addEventListener('click', openEpubOverlay);
    stickyDownloadBtn.addEventListener('click', openEpubOverlay);
    epubClose.addEventListener('click', closeEpubOverlay);
    epubCancel.addEventListener('click', closeEpubOverlay);
    epubOverlay.addEventListener('click', (e) => {
      if (e.target === epubOverlay) closeEpubOverlay();
    });

    epubAllChaptersCheckbox.addEventListener('change', () => {
      epubRangeRow.style.display = epubAllChaptersCheckbox.checked ? 'none' : 'flex';
    });
    epubCustomCss.addEventListener('change', () => {
      epubCustomCssTextarea.style.display = epubCustomCss.checked ? 'block' : 'none';
    });

    epubConfirm.addEventListener('click', async () => {
      const filename = (epubFilename.value.trim() || novel.name).replace(/[\\/:*?"<>|]/g, '_');
      const exportAll = epubAllChaptersCheckbox.checked;
      let from = Math.max(1, Number(epubFrom.value) || 1);
      let to = Math.max(1, Number(epubTo.value) || chapters.length);
      if (from > to) [from, to] = [to, from];

      const originalLabel = epubConfirm.textContent;
      epubConfirm.disabled = true;
      epubCancel.disabled = true;
      epubConfirm.textContent = 'Генерую…';

      try {
        const resp = await fetch('/api/export/epub', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            pluginId,
            novelPath,
            filename,
            exportAll,
            from: exportAll ? undefined : from,
            to: exportAll ? undefined : to,
            applySiteTheme: epubSiteTheme.checked,
            includeCustomCss: epubCustomCss.checked,
            customCss: epubCustomCss.checked ? epubCustomCssTextarea.value : undefined,
          }),
        });

        if (!resp.ok) {
          let message = 'Не вдалося згенерувати epub';
          try {
            const data = await resp.json();
            if (data?.error) message = data.error;
          } catch {
            /* тіло відповіді не JSON — лишаємо дефолтне повідомлення */
          }
          throw new Error(message);
        }

        const blob = await resp.blob();
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = `${filename}.epub`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(blobUrl);

        toast(`Готово: "${filename}.epub"`);
        closeEpubOverlay();
      } catch (err) {
        toast((err as Error).message);
      } finally {
        epubConfirm.disabled = false;
        epubCancel.disabled = false;
        epubConfirm.textContent = originalLabel;
      }
    });
  } else {
    heroDownloadBtn.style.display = 'none';
    stickyDownloadBtn.style.display = 'none';
  }

  chaptersCountEl.textContent = `${chapters.length} ${pluralChapters(chapters.length)}`;
  renderGroupPicker();
  renderChapterList();

  return {
    // Перемальовує список зі свіжими даними (novel/chapters/readSet), не
    // перепідключаючи жоден з обробників подій вище (вони вже підключені
    // рівно один раз при першому виклику setupChaptersSection).
    update(newNovel: SourceNovel, newChapters: ChapterItem[], newReadSet: Set<string>) {
      novel = newNovel;
      chapters = newChapters;
      readSet = newReadSet;
      // Томи могли з'явитись/змінитись у свіжих даних (кеш був без них) — прибираємо приховані, яких уже немає, й оновлюємо поле.
      const liveKeys = new Set(chapters.map((c) => c.volumeKey).filter(Boolean) as string[]);
      for (const k of [...hiddenVolumes]) if (!liveKeys.has(k)) hiddenVolumes.delete(k);
      paintVolumesButton();
      if (selectionMode) exitSelection();
      chaptersCountEl.textContent = `${chapters.length} ${pluralChapters(chapters.length)}`;
      renderGroupPicker();
      renderChapterList();
    },
  };
}

function setupMigration(pluginId: string, path: string, novel: SourceNovel, allPlugins: PluginInfo[]) {
  const migrateBtn = document.getElementById('migrate-btn') as HTMLButtonElement;
  const overlay = document.getElementById('migrate-overlay') as HTMLElement;
  const closeBtn = document.getElementById('migrate-close') as HTMLElement;
  const pluginsBox = document.getElementById('migrate-plugins') as HTMLElement;
  const searchBlock = document.getElementById('migrate-search-block') as HTMLElement;
  const searchInput = document.getElementById('migrate-search-input') as HTMLInputElement;
  const resultsBox = document.getElementById('migrate-results') as HTMLElement;

  function openPluginList() {
    searchBlock.style.display = 'none';
    resultsBox.innerHTML = '';
    pluginsBox.style.display = 'flex';
    pluginsBox.innerHTML = '';

    // Переносити є сенс лише на джерела ТОГО САМОГО типу: манґу — на манґа-джерела,
    // новелу — на джерела новел, аніме — на аніме. Тип береться з поточного джерела.
    const currentType = allPlugins.find((p) => p.id === pluginId)?.contentType;
    const others = allPlugins.filter((p) => p.id !== pluginId && (!currentType || p.contentType === currentType));
    if (!others.length) {
      pluginsBox.innerHTML = '<div class="empty">Інших джерел цього типу не підключено.</div>';
      return;
    }
    for (const p of others) {
      const row = document.createElement('div');
      row.className = 'migrate-plugin-row';
      row.innerHTML = `<div class="srcname">${escapeHtml(p.name)}</div>`;
      row.addEventListener('click', () => openSearchFor(p));
      pluginsBox.appendChild(row);
    }
  }

  function openSearchFor(plugin: PluginInfo) {
    pluginsBox.style.display = 'none';
    searchBlock.style.display = 'block';
    searchInput.value = novel.name;
    resultsBox.innerHTML = '';

    const oldBack = searchBlock.querySelector('.migrate-back-link');
    if (oldBack) oldBack.remove();
    const backLink = document.createElement('button');
    backLink.className = 'migrate-back-link';
    backLink.textContent = '← Інше джерело';
    backLink.addEventListener('click', openPluginList);
    searchBlock.prepend(backLink);

    async function runSearch() {
      const q = searchInput.value.trim();
      resultsBox.innerHTML = '<div class="empty">Пошук…</div>';
      if (!q) {
        resultsBox.innerHTML = '';
        return;
      }
      try {
        const results = await api.search(plugin.id, q);
        renderResults(plugin, results);
      } catch (err) {
        resultsBox.innerHTML = `<div class="empty">${escapeHtml((err as Error).message)}</div>`;
      }
    }

    searchInput.oninput = () => {
      window.clearTimeout((searchInput as any)._t);
      (searchInput as any)._t = window.setTimeout(runSearch, 400);
    };
    runSearch();
  }

  function renderResults(plugin: PluginInfo, results: NovelItem[]) {
    resultsBox.innerHTML = '';
    if (!results.length) {
      resultsBox.innerHTML = '<div class="empty">Нічого не знайдено.</div>';
      return;
    }
    for (const n of results) {
      const row = document.createElement('div');
      row.className = 'migrate-result-row';
      const cover = coverEl(n.name, n.cover, plugin.id + n.path);
      const title = document.createElement('div');
      title.textContent = n.name;
      row.appendChild(cover);
      row.appendChild(title);
      row.addEventListener('click', () => doMigrate(plugin, n));
      resultsBox.appendChild(row);
    }
  }

  async function doMigrate(plugin: PluginInfo, target: NovelItem) {
    try {
      const res = await api.migrateLibrary({
        fromId: novel.id!,
        pluginId: plugin.id,
        path: target.path,
        name: target.name,
        cover: target.cover,
        categoryIds: libraryBtnState?.categoryIds,
      });
      toast('Перенесено на інше джерело');
      overlay.style.display = 'none';
      // Стрілка "назад" на новій сторінці твору має вести в БІБЛІОТЕКУ (у ту саму
      // категорію, де твір був), а не на сторінку джерела — тому додаємо from=library
      // і параметри повернення (тип, категорія, сортування, пошук), як робить сама Бібліотека.
      const cur = new URLSearchParams(location.search);
      const params = new URLSearchParams({ plugin: plugin.id, path: target.path, from: 'library', type: res.contentType });
      if (res.categoryIds?.[0]) params.set('tab', res.categoryIds[0]);
      for (const k of ['sort', 'q']) {
        const v = cur.get(k);
        if (v) params.set(k, v);
      }
      location.href = `/novel?${params.toString()}`;
    } catch (err) {
      toast((err as Error).message);
    }
  }

  migrateBtn.addEventListener('click', () => {
    overlay.style.display = 'flex';
    openPluginList();
  });
  closeBtn.addEventListener('click', () => (overlay.style.display = 'none'));
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) overlay.style.display = 'none';
  });
}

function readerUrl(
  pluginId: string,
  novelPath: string,
  novelName: string,
  cover: string | undefined,
  ch: { path: string; name: string; chapterNumber?: number },
  historyEntry?: HistoryItem,
  contentType?: 'novel' | 'manga' | 'anime',
  fromParam?: string | null,
  libReturnParams?: URLSearchParams,
): string {
  const extra = new URLSearchParams();
  if (fromParam) extra.set('from', fromParam);
  if (libReturnParams) for (const [k, v] of libReturnParams) extra.set(k, v);
  // Відновлення позиції в розділі (тільки якщо це саме той розділ, що зберігся в історії).
  if (historyEntry && historyEntry.chapterPath === ch.path) {
    if (contentType === 'manga') {
      if (historyEntry.page) extra.set('page', String(historyEntry.page));
    } else if (historyEntry.progress > 0 && historyEntry.progress < 100) {
      extra.set('progress', String(historyEntry.progress));
    }
  }

  // У короткому посиланні /chapterM (чи /volNchapterM) M/N — це САМЕ ТЕ
  // число(-а), що показує джерело (ch.chapterNumber, за угодою
  // chapter-volume.ts — з томом, закодованим у старший розряд), А НЕ
  // позиція в списку: buildPrettyUrl/switchToPrettyUrl декодують том і
  // розділ з нього самі.
  const cachedSlug = getCachedSlug(pluginId, novelPath);
  if (cachedSlug && ch.chapterNumber !== undefined) {
    const suffix = chapterUrlSuffix(ch.chapterNumber);
    const qsStr = extra.toString();
    return `${prettyWorkPath(pluginId, cachedSlug, suffix)}${qsStr ? `?${qsStr}` : ''}`;
  }

  const params = new URLSearchParams({
    plugin: pluginId,
    novelPath,
    novelName,
    path: ch.path,
    name: ch.name,
  });
  if (ch.chapterNumber !== undefined) params.set('num', String(ch.chapterNumber));
  if (cover) params.set('cover', cover);
  for (const [k, v] of extra) params.set(k, v);
  return `/reader?${params.toString()}`;
}

function pluralChapters(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'розділ';
  if ([2, 3, 4].includes(mod10) && ![12, 13, 14].includes(mod100)) return 'розділи';
  return 'розділів';
}

function escapeHtml(s: string): string {
  const d = document.createElement('div');
  d.textContent = s;
  return d.innerHTML;
}
